import { useState } from 'react';
import { Task } from '../../lib/types';
import { useDeleteTask } from '../../lib/api/tasks';
import { useToast } from '../ui/Toast';
import CreateTaskModal from '../modals/CreateTaskModal';
import ConfirmDialog from '../ui/ConfirmDialog';

/** Shared hook: returns (openEdit, openDelete, modals). Drop {modals} once inside any page
 *  that lists tasks, and pass openEdit/openDelete as TaskRow's onEdit/onDelete props. */
export const useTaskActions = () => {
  const toast = useToast();
  const deleteTask = useDeleteTask();
  const [editTarget, setEditTarget] = useState<Task | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);

  const openEdit = (task: Task) => setEditTarget(task);
  const openDelete = (task: Task) => setDeleteTarget(task);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTask.mutateAsync(deleteTarget.id);
      toast.push('Task deleted');
    } catch {
      toast.push('Could not delete task');
    }
    setDeleteTarget(null);
  };

  const modals = (
    <>
      <CreateTaskModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        task={editTarget}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={`Delete "${deleteTarget?.name ?? 'task'}"?`}
        description="This will permanently remove the task. This cannot be undone."
        confirmLabel="Delete task"
        danger
        busy={deleteTask.isPending}
      />
    </>
  );

  return { openEdit, openDelete, modals };
};
